import { db } from '../config/firebase';
import { doc, getDoc, setDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';

const SCHEDULE_COLLECTION = 'campaignSchedule';
const MAIN_SCHEDULE_DOC_ID = 'main'; // Single document to store the schedule

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Recalculates the best day for D&D sessions based on all user preferences
 * and updates the main schedule document in Firestore.
 */
export const recalculateAndStoreBestDay = async () => {
  try {
    const usersCollectionRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersCollectionRef);

    const availabilityCounts = daysOfWeek.reduce((acc, day) => ({ ...acc, [day]: 0 }), {});
    const vetoesByDay = daysOfWeek.reduce((acc, day) => ({ ...acc, [day]: [] }), {});
    const preferenceScores = daysOfWeek.reduce((acc, day) => ({ ...acc, [day]: 0 }), {}); // For tie-breaking
    let participantCount = 0;
    const userPreferences = []; // To store preferredDays for tie-breaking

    usersSnapshot.forEach(userDoc => {
      const userData = userDoc.data();
      participantCount++;
      if (userData.availableDays && Array.isArray(userData.availableDays)) {
        userData.availableDays.forEach(day => {
          if (availabilityCounts.hasOwnProperty(day)) {
            availabilityCounts[day]++;
          }
        });
      }
      if (userData.vetoDay && vetoesByDay.hasOwnProperty(userData.vetoDay)) {
        vetoesByDay[userData.vetoDay].push(userDoc.id);
      }
      // Store preferred days for each user for tie-breaking
      if (userData.preferredDays && Array.isArray(userData.preferredDays)) {
        userPreferences.push({ userId: userDoc.id, preferences: userData.preferredDays });
        userData.preferredDays.forEach((day, index) => {
          if (preferenceScores.hasOwnProperty(day)) {
            // Higher preference (lower index) gets more points
            // e.g., #1 gets 3 points, #2 gets 2, #3 gets 1
            preferenceScores[day] += (3 - index); 
          }
        });
      }
    });

    let bestDay = null;
    let maxAvailability = -1;
    let highestPreferenceScore = -1;

    daysOfWeek.forEach(day => {
      if (vetoesByDay[day].length === 0) { // Day is not vetoed by anyone
        const currentAvailability = availabilityCounts[day];
        const currentPreferenceScore = preferenceScores[day] || 0;

        if (currentAvailability > maxAvailability) {
          maxAvailability = currentAvailability;
          highestPreferenceScore = currentPreferenceScore;
          bestDay = day;
        } else if (currentAvailability === maxAvailability) {
          // Tie in availability, use preference scores to break the tie
          if (currentPreferenceScore > highestPreferenceScore) {
            highestPreferenceScore = currentPreferenceScore;
            bestDay = day;
          } else if (currentPreferenceScore === highestPreferenceScore) {
            // If still tied (e.g. no preferences or same scores), keep the first one found or handle as needed.
            // For now, if scores are also tied, the existing bestDay (earlier in week or first encountered) is kept.
          }
        }
      }
    });

    const scheduleData = {
      bestCalculatedDay: bestDay,
      availabilityScore: availabilityCounts,
      vetoes: vetoesByDay,
      preferenceScores: preferenceScores, // Add preference scores to the schedule data
      participantCount: participantCount,
      lastUpdated: serverTimestamp(),
    };

    const scheduleDocRef = doc(db, SCHEDULE_COLLECTION, MAIN_SCHEDULE_DOC_ID);
    await setDoc(scheduleDocRef, scheduleData);
    console.log('Campaign schedule recalculated and updated:', scheduleData);
    return scheduleData;
  } catch (error) {
    console.error("Error recalculating and storing best day:", error);
    throw error;
  }
};

/**
 * Fetches the current planned D&D schedule from Firestore.
 * @returns {Promise<object|null>} The schedule data or null if not found.
 */
export const getPlannedSchedule = async () => {
  try {
    const scheduleDocRef = doc(db, SCHEDULE_COLLECTION, MAIN_SCHEDULE_DOC_ID);
    const docSnap = await getDoc(scheduleDocRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("No schedule document found. Consider creating one or running recalculate.");
      // Optionally, trigger a recalculation if not found
      // return recalculateAndStoreBestDay();
      return null;
    }
  } catch (error) {
    console.error("Error fetching planned schedule:", error);
    throw error;
  }
};
